import React, { useState, useEffect, use } from 'react';
import {
    Card, CardBody, CardTitle, CardText, Button, Row, Col, Modal, ModalHeader,
    ModalBody, ModalFooter, Form, FormGroup, Label, Input, Table, Nav, NavItem,
    NavLink, TabContent, TabPane, Badge,
    Container,
    Spinner
} from 'reactstrap';
import {
    FaWarehouse, FaMapMarkerAlt, FaMoneyBillWave, FaPlus, FaTimes,
    FaArrowDown, FaArrowUp, FaExchangeAlt, FaBoxOpen, FaTruck, FaChartLine,
    FaFileAlt, FaFileInvoice,
    FaStar, FaEdit
} from 'react-icons/fa';
import classnames from 'classnames';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import * as Yup from 'yup';
import { entrepotActions } from '../../sagas/entrepotSlice';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../../helpers/utility';

const EntrepotPage = () => {

    const dispatch = useDispatch();
    const { entrepots, loading, receptions, deliveries } = useSelector((state) => state.entrepots);
    const { t } = useTranslation();
    // State for details modal
    const [detailsModal, setDetailsModal] = useState(false);
    const [selectedEntrepot, setSelectedEntrepot] = useState(null);
    const [activeTab, setActiveTab] = useState('1');
    const [editModal, setEditModal] = useState(false);
    const [editEntrepot, setEditEntrepot] = useState(null);
    // State for add new entrepot modal
    const [addModal, setAddModal] = useState(false);
    const [newEntrepot, setNewEntrepot] = useState({
        name: '',
        address: '',
        stockValue: 0
    });

    useEffect(() => {
        dispatch(entrepotActions.list());
    }, []);


    // Current year for reference formatting
    const currentYear = new Date().getFullYear();

    const [movements, setMovements] = useState([]);

    // Toggle for details modal
    const toggleDetailsModal = () => {
        setDetailsModal(!detailsModal);
        if (detailsModal) {
            setSelectedEntrepot(null);
            setActiveTab('1');
        }
    };

    // Toggle for add modal
    const toggleAddModal = () => {
        setAddModal(!addModal);
        if (addModal) {
            setNewEntrepot({
                name: '',
                address: '',
                stockValue: 0
            });
        }
    };

    const fetchReceptions = (entrepotId) => {
        dispatch(entrepotActions.getLatestReceptions(entrepotId));
    }

    const fetchDeliveries = (entrepotId) => {
        dispatch(entrepotActions.getLatestDeliveries(entrepotId));
    }

    // Handle details button click
    const handleDetailsClick = (entrepotId) => {
        fetchReceptions(entrepotId);
        fetchDeliveries(entrepotId);
        const entrepot = entrepots.find(e => e.id === entrepotId);
        setSelectedEntrepot(entrepot);
        setDetailsModal(true);
    };

    // Handle tab change
    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    // Handle input change for new entrepot
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewEntrepot({
            ...newEntrepot,
            [name]: name === 'stockValue' ? parseFloat(value) || 0 : value
        });
    };

    // Format currency
    const formatPrice = (amount) => {
        return amount.toLocaleString('fr-FR') + ' MAD';
    };

    // Format reference number with padding
    const formatReference = (prefix, number, year) => {
        return `${prefix}-${String(number).padStart(4, '0')}-${year}`;
    };

    const formik = useFormik({
        initialValues: {
            code: '',
            libelle: '',
            adresse: '',
            actif: true,
            stockValue: 0,
            principal: false,
        },
        validationSchema: Yup.object({
            libelle: Yup.string().required('Le nom est obligatoire'),
            adresse: Yup.string().required('L\'adresse est obligatoire'),
        }),
        onSubmit: (values, { resetForm }) => {
            let payload = {
                data: values,
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: 'Enregistrement réussi',
                        text: 'L\'entrepôt a été ajouté avec succès.',
                    });
                    resetForm();
                    toggleAddModal();
                }
            };
            dispatch(entrepotActions.addEntrepot(payload));
        }
    });

    const { values, setFieldValue, getFieldProps, handleSubmit, errors } = formik;

    // Add a toggle function for edit modal
    const toggleEditModal = () => {
        setEditModal(!editModal);
        if (editModal) {
            setEditEntrepot(null);
        }
    };

    // Add a function to handle edit button click
    const handleEditClick = (entrepotId) => {
        const entrepot = entrepots.find(e => e.id === entrepotId);
        setEditEntrepot(entrepot);
        setEditModal(true);
    };
    // Add a function to handle delete button click 
    console.log(receptions);
    console.log(deliveries);
    return (
        <>
            {loading ? ( // Show spinner when loading
                <div className="text-center py-5">
                    <Spinner color="primary" />
                    <div className="mt-2 text-muted">{t('common.loading')}</div>
                </div>
            ) : (
                <Container fluid>
                    <Row className="mb-4">
                        <Col>
                            <Card className="shadow-sm">
                                <CardBody className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0 d-flex align-items-center">
                                        <FaWarehouse className="text-primary me-2" />
                                        {t('entrepot.title')}
                                    </h4>
                                    <div className="d-flex align-items-center">
                                        <Button color="primary" onClick={toggleAddModal}>
                                            <FaPlus className="mr-2" /> {t('entrepot.new')}
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        {entrepots.map((entrepot) => (
                            <Col md={4} key={entrepot.id} className="mb-4">
                                <Card className="shadow-sm h-100">
                                    <CardBody>
                                        <CardTitle tag="h5" className="d-flex align-items-center">
                                            <FaWarehouse className="me-2 text-primary" />
                                            {entrepot.code} - {entrepot.libelle}
                                            {entrepot.principal && (
                                                <Badge color="warning" className="ms-2" title={t('entrepot.defaultWarehouse')}>
                                                    <FaStar className="me-1" /> {t('entrepot.default')}
                                                </Badge>
                                            )}
                                        </CardTitle>
                                        <CardText className="d-flex align-items-center">
                                            <FaMapMarkerAlt className="me-2 text-muted" />
                                            {entrepot.adresse}
                                        </CardText>
                                        {/* <CardText className="d-flex align-items-center">
                                            <FaMoneyBillWave className={`me-2 ${entrepot.stockValue < 0 ? 'text-danger' : 'text-success'}`} />
                                            Valeur actuelle du stock :
                                            <span className={entrepot.stockValue < 0 ? 'text-danger ms-1' : 'ms-1'}>
                                                {entrepot.stockValue.toLocaleString('fr-FR')} MAD
                                            </span>
                                        </CardText> */}
                                        {/* <div className="d-flex">
                                            <Button
                                                color="primary"
                                                className="me-2"
                                                onClick={() => handleDetailsClick(entrepot.id)}
                                            >
                                                Voir les détails
                                            </Button>
                                            <Button
                                                color="warning"
                                                onClick={() => handleEditClick(entrepot.id)}
                                            >
                                                <FaEdit className="me-1" /> {t('common.edit')}
                                            </Button>
                                        </div> */}
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                        {entrepots.length === 0 && (
                            <Col md={12} className="text-center">
                                <h5>Aucun entrepôt trouvé</h5>
                                <p>Veuillez ajouter un entrepôt pour commencer.</p>
                            </Col>
                        )}
                    </Row>

                    {/* Details Modal */}
                    <Modal isOpen={detailsModal} toggle={toggleDetailsModal} size="lg">
                        <ModalHeader toggle={toggleDetailsModal}>
                            {selectedEntrepot && (
                                <span>
                                    <FaWarehouse className="me-2" />
                                    {selectedEntrepot.code + ' - ' + selectedEntrepot.libelle}
                                </span>
                            )}
                        </ModalHeader>
                        <ModalBody>
                            {selectedEntrepot && (
                                <>
                                    <div className="mb-4">
                                        <h6 className="text-muted mb-3">Informations générales</h6>
                                        <Row>
                                            <Col md={6}>
                                                <p><strong>Adresse:</strong> {selectedEntrepot.adresse}</p>
                                            </Col>
                                            <Col md={6}>
                                                <p><strong>Valeur du stock:</strong> {formatPrice(selectedEntrepot.stockValue)}</p>
                                            </Col>
                                        </Row>
                                    </div>

                                    <Nav tabs>
                                        <NavItem>
                                            <NavLink
                                                className={classnames({ active: activeTab === '1' })}
                                                onClick={() => { toggleTab('1'); }}
                                            >
                                                <FaArrowDown className="me-1" /> Réceptions
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                className={classnames({ active: activeTab === '2' })}
                                                onClick={() => { toggleTab('2'); }}
                                            >
                                                <FaArrowUp className="me-1" /> Livraisons
                                            </NavLink>
                                        </NavItem>
                                        {/* <NavItem>
                                            <NavLink
                                                className={classnames({ active: activeTab === '3' })}
                                                onClick={() => { toggleTab('3'); }}
                                            >
                                                <FaExchangeAlt className="me-1" /> Mouvements de stock
                                            </NavLink>
                                        </NavItem> */}
                                    </Nav>

                                    <TabContent activeTab={activeTab} className="pt-3">
                                        <TabPane tabId="1">
                                            <h6 className="mb-3">
                                                <FaBoxOpen className="me-2 text-primary" />
                                                Dernières réceptions
                                            </h6>
                                            <Table responsive striped hover>
                                                <thead>
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Référence</th>
                                                        <th>Fournisseur</th>
                                                        <th>Articles</th>
                                                        <th>Valeur</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {receptions.map(reception => (
                                                        <tr key={reception.id}>
                                                            <td>{reception.dateReception}</td>
                                                            <td>
                                                                <Badge color="info" className="text-white">
                                                                    <FaFileAlt className="me-1" />
                                                                    {reception.numero}
                                                                </Badge>
                                                            </td>
                                                            <td>{reception.fournisseurNom}</td>
                                                            <td>{reception.lignes.length}</td>
                                                            <td>{formatPrice(reception.montantTotalTTC)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </TabPane>

                                        <TabPane tabId="2">
                                            <h6 className="mb-3">
                                                <FaTruck className="me-2 text-primary" />
                                                Dernières livraisons
                                            </h6>
                                            <Table responsive striped hover>
                                                <thead>
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Référence</th>
                                                        <th>N° Commande</th>
                                                        <th>Client</th>
                                                        <th>Articles</th>
                                                        <th>Valeur</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {deliveries.map(delivery => (
                                                        <tr key={delivery.id}>
                                                            <td>{delivery.dateLivraison}</td>
                                                            <td>
                                                                <Badge color="primary" className="text-white">
                                                                    <FaFileInvoice className="me-1" />
                                                                    {delivery.numero}
                                                                </Badge>
                                                            </td>
                                                            <td>{delivery.commandeNumero || 'N/A'}</td>
                                                            <td>{delivery.clientNom}</td>
                                                            <td>{delivery.lignes.length}</td>
                                                            <td>{formatPrice(delivery.montantTotalTTC)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </TabPane>

                                        {/* <TabPane tabId="3">
                                            <h6 className="mb-3">
                                                <FaChartLine className="me-2 text-primary" />
                                                Mouvements de stock récents
                                            </h6>
                                            <Table responsive striped hover>
                                                <thead>
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Référence</th>
                                                        <th>Document</th>
                                                        <th>Produit</th>
                                                        <th>Type</th>
                                                        <th>Quantité</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {movements.map(movement => (
                                                        <tr key={movement.id}>
                                                            <td>{movement.date}</td>
                                                            <td>
                                                                {movement.type === 'Entrée' ? (
                                                                    <Badge color="info" className="text-white">
                                                                        <FaFileAlt className="me-1" />
                                                                        {movement.reference}
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge color="primary" className="text-white">
                                                                        <FaFileInvoice className="me-1" />
                                                                        {movement.reference}
                                                                    </Badge>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {movement.documentRef}
                                                            </td>
                                                            <td>{movement.product}</td>
                                                            <td>
                                                                {movement.type === 'Entrée' ? (
                                                                    <Badge color="success">
                                                                        <FaArrowDown className="me-1" /> Entrée
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge color="danger">
                                                                        <FaArrowUp className="me-1" /> Sortie
                                                                    </Badge>
                                                                )}
                                                            </td>
                                                            <td>{movement.quantity}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </TabPane> */}
                                    </TabContent>
                                </>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" onClick={toggleDetailsModal}>
                                <FaTimes className="me-1" /> Fermer
                            </Button>
                        </ModalFooter>
                    </Modal>

                    {/* Add New Entrepot Modal */}
                    <Modal isOpen={addModal} toggle={toggleAddModal}>
                        <Form onSubmit={handleSubmit}>
                            <ModalHeader toggle={toggleAddModal}>
                                <FaPlus className="me-2" />
                                Ajouter un nouvel entrepôt
                            </ModalHeader>
                            <ModalBody>
                                <FormGroup>
                                    <Label for="libelle">Nom de l'entrepôt *</Label>
                                    <Input
                                        type="text"
                                        name="libelle"
                                        id="libelle"
                                        placeholder="Entrez le nom de l'entrepôt"
                                        {...getFieldProps('libelle')}
                                        required
                                    />
                                    {errors.libelle && <div className="text-danger">{errors.libelle}</div>}
                                </FormGroup>
                                <FormGroup>
                                    <Label for="address">Adresse *</Label>
                                    <Input
                                        type="text"
                                        name="address"
                                        id="address"
                                        placeholder="Entrez l'adresse de l'entrepôt"
                                        {...getFieldProps('adresse')}
                                        required
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="stockValue">Valeur initiale du stock</Label>
                                    <Input
                                        type="number"
                                        name="stockValue"
                                        id="stockValue"
                                        placeholder="0"
                                        {...getFieldProps('stockValue')}
                                        disabled
                                    />
                                </FormGroup>
                                <FormGroup check className="mb-3">
                                    <Label check>
                                        <Input
                                            type="checkbox"
                                            name="principal"
                                            id="default"
                                            {...getFieldProps('principal')}
                                        />
                                        {' '}
                                        Définir comme entrepôt par défaut
                                    </Label>
                                    <small className="form-text text-muted">
                                        L'entrepôt par défaut sera utilisé automatiquement pour les nouvelles opérations.
                                    </small>
                                </FormGroup>

                            </ModalBody>
                            <ModalFooter>
                                <Button color="primary" type="submit">
                                    <FaPlus className="me-1" /> Ajouter
                                </Button>
                                <Button color="secondary" onClick={toggleAddModal}>
                                    <FaTimes className="me-1" /> Annuler
                                </Button>
                            </ModalFooter>
                        </Form>
                    </Modal>
                    <Modal isOpen={editModal} toggle={toggleEditModal}>
                        <Form onSubmit={(e) => {
                            e.preventDefault();
                            const updatedEntrepot = {
                                id: editEntrepot.id,
                                code: editEntrepot.code,
                                libelle: e.target.libelle.value,
                                adresse: e.target.adresse.value,
                                principal: e.target.principal.checked,
                                stockValue: editEntrepot.stockValue,
                                actif: true
                            };

                            let payload = {
                                id: editEntrepot.id,
                                data: updatedEntrepot,
                                onSuccess: () => {
                                    Swal.fire({
                                        icon: 'success',
                                        title: 'Mise à jour réussie',
                                        text: 'L\'entrepôt a été modifié avec succès.',
                                    });
                                    toggleEditModal();
                                    dispatch(entrepotActions.list());
                                }
                            };
                            dispatch(entrepotActions.updateEntrepot(payload));
                        }}>
                            <ModalHeader toggle={toggleEditModal}>
                                <FaEdit className="me-2" />
                                Modifier l'entrepôt
                            </ModalHeader>
                            <ModalBody>
                                {editEntrepot && (
                                    <>
                                        <FormGroup>
                                            <Label for="libelle">Nom de l'entrepôt *</Label>
                                            <Input
                                                type="text"
                                                name="libelle"
                                                id="libelle"
                                                defaultValue={editEntrepot.libelle}
                                                required
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label for="adresse">Adresse *</Label>
                                            <Input
                                                type="text"
                                                name="adresse"
                                                id="adresse"
                                                defaultValue={editEntrepot.adresse}
                                                required
                                            />
                                        </FormGroup>
                                        <FormGroup check className="mb-3">
                                            <Label check>
                                                <Input
                                                    type="checkbox"
                                                    name="principal"
                                                    id="default"
                                                    defaultChecked={editEntrepot.principal}
                                                />
                                                {' '}
                                                Définir comme entrepôt par défaut
                                            </Label>
                                            <small className="form-text text-muted">
                                                L'entrepôt par défaut sera utilisé automatiquement pour les nouvelles opérations.
                                            </small>
                                        </FormGroup>
                                    </>
                                )}
                            </ModalBody>
                            <ModalFooter>
                                <Button color="primary" type="submit">
                                    <FaEdit className="me-1" /> Mettre à jour
                                </Button>
                                <Button color="secondary" onClick={toggleEditModal}>
                                    <FaTimes className="me-1" /> Annuler
                                </Button>
                            </ModalFooter>
                        </Form>
                    </Modal>
                </Container>)}
        </>
    );
};

export default EntrepotPage;
